/* Knives & Stones signup — trigger engine + submit + step switching.
 * No third-party dependencies. Vanilla JS, IIFE, 'use strict'.
 *
 * Contract with the backend (marketing_os/signup/routes.py — read it, don't guess):
 *   POST /apps/tripcoins/signup  (Shopify strips its subpath -> /apps/kns/signup)
 *   Content-Type: application/json
 *   body: {"email": "...", "source": "popup"|"landing_flow"|"landing_30"}
 *   The email travels in the BODY only — never a query param (no PII in URLs).
 *   The response is GENERIC for every outcome (new / returning / suppressed /
 *   honeypot): ANY 2xx renders the SAME success step. Only a 400 with
 *   {"status":"invalid"} means the address itself was rejected and is surfaced
 *   as a form error. A 429 shows an honest "try again in a moment".
 *
 * Never-throw guarantee: the entry point is wrapped so ANY failure (missing
 * markup, dead CSSOM, blocked storage) degrades to "no popup" — never to a
 * broken page. A capture form that breaks a PDP costs more than the signups.
 */
(() => {
  'use strict';

  if (window.KNSSignup) return;
  window.KNSSignup = true;

  const API_URL = '/apps/tripcoins/signup';
  const REQUEST_TIMEOUT_MS = 10000;

  // Dismissal window: after NO, THANKS / close, hide the popup for 7 days and
  // show the teaser instead. Rationale in README (matches Klaviyo's effective
  // behaviour for a teaser-enabled form; short enough to re-ask a seasonal
  // shopper, long enough to not nag).
  const COOKIE_SUBSCRIBED = 'kns_signup_sub'; // set after a successful submit
  const COOKIE_DISMISSED = 'kns_signup_dismiss'; // set on dismiss/close
  const COOKIE_DAYS_SUBSCRIBED = 365;
  const DEFAULT_DISMISS_DAYS = 7;
  // Phones get the automatic invitation back as a gentle bottom sheet (James,
  // 26 Oct: "we needed the pop up on phones for sure … yes option 1 but not too
  // annoying"). Because it interrupts a one-handed session, its quiet window is
  // LONGER than the desktop popup's, and it fires at most once per session.
  const DEFAULT_SHEET_DISMISS_DAYS = 14;
  const SHEET_MIN_VISIBLE_MS = 5000; // a hard floor: never before 5s, whatever is configured
  const SESSION_SHEET_SHOWN = 'kns_signup_sheet_shown'; // once per browser session
  // Every control this file must never sit on, shared by the bar's rect test and
  // the sheet's viewport test (one list, so the two can never drift).
  const BUY_CONTROL_SELECTOR = 'form[action*="/cart/add"] button, form[action*="/cart/add"] input:not([type="hidden"]), form[action*="/cart/add"] select, quantity-input, .shopify-payment-button, shopify-accelerated-checkout';

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* ---------- tiny safe helpers (storage/cookie can throw in private modes) --- */

  function safe(fn, fallback) {
    try { return fn(); } catch (_) { return fallback; }
  }

  function setCookie(name, days) {
    safe(() => {
      const d = new Date(Date.now() + days * 864e5);
      document.cookie = name + '=1; expires=' + d.toUTCString() +
        '; path=/; SameSite=Lax';
    });
  }

  function hasCookie(name) {
    return safe(
      () => document.cookie.split(';').some(c => c.trim().startsWith(name + '=')),
      false
    );
  }

  /* ---------- the reusable form component (popup AND landing embeds) --------- */

  function initForm(root) {
    if (!root || root.dataset.ksuBound) return;
    root.dataset.ksuBound = '1';

    const source = root.dataset.ksuSource || 'popup';
    const layout = root.dataset.ksuLayout || 'popup';
    const form = root.querySelector('[data-ksu-submit]');
    if (!form) return;
    const emailInput = form.querySelector('input[name="email"]');
    const btn = root.querySelector('[data-ksu-submit-btn]');
    const errorEl = root.querySelector('[data-ksu-error]');
    const dismissBtn = root.querySelector('[data-ksu-dismiss]');
    const suggestEl = root.querySelector('[data-ksu-suggest]');

    /* ---------- locale messages (SIGNUP-STRINGS, 26 Oct 2026) ----------------
     * The seven messages below used to be English literals in this file, so
     * .co.kr and .com.tw visitors saw English errors and an English typo nudge.
     * They are now kns_local.signup.* keys, rendered by the snippet onto the
     * FORM element as data-ksu-msg-<name> attributes, and read here per form.
     * Every message keeps its CURRENT English string as the fallback, so old
     * cached HTML or a theme copy without the attributes behaves exactly as
     * today (never-throw contract: a missing attribute is "use English", never
     * an empty error line). */
    const MSG = {
      suggest: ['ksuMsgSuggest', 'Did you mean {domain}?'],
      invalidEmail: ['ksuMsgErrorInvalidEmail', 'Please enter a valid email address.'],
      submitting: ['ksuMsgSubmitting', 'SUBMITTING…'],
      rejectedEmail: ['ksuMsgErrorRejectedEmail', 'That email address doesn’t look right — please check it and try again.'],
      rateLimited: ['ksuMsgErrorRateLimited', 'Too many attempts — please wait a moment and try again.'],
      server: ['ksuMsgErrorServer', 'Something went wrong on our end — please try again.'],
      network: ['ksuMsgErrorNetwork', 'We couldn’t reach the server — please check your connection and try again.'],
    };
    function msg(name) {
      const v = form.dataset[MSG[name][0]];
      return typeof v === 'string' && v !== '' ? v : MSG[name][1];
    }

    /* ---------- domain typo nudge (assets/kns-email-typo.js) ---------------
     * Production bounces show people losing the newsletter to `gmail.con`,
     * `gamil.com`, `gnail.com` and friends — all syntactically valid, so
     * EMAIL_RE above passes them and always will.
     *
     * It is a NUDGE, never a gate: nothing here touches the submit path, and
     * the visitor can always send exactly what they typed. Read the module
     * lazily off `window` on every call so a missing or late asset degrades to
     * "no suggestion" rather than a broken form — same never-throw contract as
     * the rest of this file. */
    const SUGGEST_DEBOUNCE_MS = 400;
    let suggestTimer = null;
    let shownSuggestion = ''; // the correction currently on screen, if any

    function clearSuggestion() {
      if (suggestTimer) { clearTimeout(suggestTimer); suggestTimer = null; }
      shownSuggestion = '';
      if (suggestEl) suggestEl.textContent = '';
    }

    function renderSuggestion(corrected) {
      // Built with DOM nodes, never innerHTML. The domain comes from the
      // module's own constant list, but keeping the boundary at textContent
      // means it stays safe if that list ever becomes data-driven.
      //
      // The whole sentence is the button — see the snippet for why (full-width
      // tap target on a phone; one control whose name is the whole question).
      suggestEl.textContent = '';
      shownSuggestion = corrected;
      const accept = document.createElement('button');
      accept.type = 'button'; // never submits
      accept.className = 'ksu-suggest-accept';
      const domain = document.createElement('strong');
      domain.textContent = corrected.slice(corrected.lastIndexOf('@') + 1);
      // The sentence is the locale's `suggest` value split on the {domain}
      // placeholder: text node, <strong>, text node — still text nodes only,
      // so a locale file can never become an injection surface. A value
      // without {domain} (a bad translation shipped without the placeholder)
      // falls back to today's English construction rather than printing the
      // literal token.
      const sentence = msg('suggest');
      const at = sentence.indexOf('{domain}');
      if (at !== -1) {
        accept.appendChild(document.createTextNode(sentence.slice(0, at)));
        accept.appendChild(domain);
        accept.appendChild(document.createTextNode(sentence.slice(at + '{domain}'.length)));
      } else {
        accept.appendChild(document.createTextNode('Did you mean '));
        accept.appendChild(domain);
        accept.appendChild(document.createTextNode('?'));
      }
      accept.addEventListener('click', () => {
        safe(() => {
          if (emailInput) {
            emailInput.value = corrected;
            // Themes and password managers listen for this; without it a
            // corrected field can be read back stale by other page scripts.
            emailInput.dispatchEvent(new Event('input', { bubbles: true }));
            emailInput.focus();
          }
          clearSuggestion();
          clearError();
        });
      });
      suggestEl.appendChild(accept);
    }

    function checkSuggestion() {
      if (!suggestEl || !emailInput) return;
      const api = window.KnsEmailTypo;
      if (!api || typeof api.suggest !== 'function') return;
      const corrected = api.suggest(emailInput.value);
      if (!corrected) { clearSuggestion(); return; }
      // Already offering this exact correction: leave the existing button in
      // place. Re-rendering it here would DESTROY THE BUTTON MID-TAP — the blur
      // that runs this check is the same gesture that is pressing the
      // suggestion, and a click whose mousedown target left the DOM is never
      // dispatched to it, so the accept silently does nothing.
      if (corrected === shownSuggestion) return;
      // Rebuilding the whole line is what makes the live region announce, and
      // it drops the previous button (and its listener) with it.
      renderSuggestion(corrected);
    }

    if (suggestEl && emailInput) {
      // Debounced while typing so the half-finished "…@gmail.co" on the way to
      // "…@gmail.com" cannot flash a suggestion at the visitor. Checked again on
      // blur, without the delay, because on a phone the tap that leaves the
      // field is usually the same gesture that reaches for SIGN ME UP.
      emailInput.addEventListener('input', () => {
        safe(() => {
          clearSuggestion();
          suggestTimer = setTimeout(() => safe(checkSuggestion), SUGGEST_DEBOUNCE_MS);
        });
      });
      emailInput.addEventListener('blur', () => safe(checkSuggestion));
    }


    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }
    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    function showSuccess() {
      // A pending debounce must not repaint a live region on the step the
      // visitor has already left.
      clearSuggestion();
      const steps = root.querySelectorAll('[data-ksu-step]');
      steps.forEach(s => { s.hidden = s.dataset.ksuStep !== 'success'; });
      const heading = root.querySelector('[data-ksu-success-heading]');
      if (heading && typeof heading.focus === 'function') heading.focus();
      // A successful submit suppresses the popup on this device (first-party
      // signal 2 — see README). Clearing the dismissal state too, since
      // "subscribed" is the stronger signal.
      setCookie(COOKIE_SUBSCRIBED, COOKIE_DAYS_SUBSCRIBED);
      // Tell the popup engine it is spent so its cookie-banner watch stops
      // too (fix-3 item 2); optional — the engine may not be booted.
      const spentPopup = document.querySelector('[data-ksu-popup]');
      if (spentPopup && spentPopup.__ksuSpent) safe(spentPopup.__ksuSpent);
    }

    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => {
        const popup = document.querySelector('[data-ksu-popup]');
        if (popup && popup.__ksuDismiss) popup.__ksuDismiss();
      });
    }

    form.addEventListener('submit', ev => {
      ev.preventDefault();
      safe(() => {
        clearError();
        const email = (emailInput && emailInput.value || '').trim();
        if (!EMAIL_RE.test(email)) {
          showError(msg('invalidEmail'));
          if (emailInput) emailInput.focus();
          return;
        }
        // Honeypot values are posted verbatim — the backend quietly accepts and
        // captures nothing. No client-side branching on them (a client that
        // behaves differently for filled honeypots would leak the check).
        const payload = { email, source };
        ['website', 'hp'].forEach(name => {
          const hp = form.querySelector('input[name="' + name + '"]');
          if (hp && hp.value) payload[name] = hp.value;
        });

        if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
        const originalLabel = btn ? btn.textContent : '';
        if (btn) btn.textContent = msg('submitting');

        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = controller
          ? setTimeout(() => { try { controller.abort(); } catch (_) {} }, REQUEST_TIMEOUT_MS)
          : null;

        fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller ? controller.signal : undefined,
          credentials: 'same-origin',
        }).then(res => {
          if (res.ok) {
            // GENERIC response by design — never distinguish new/returning/
            // suppressed/honeypot. Any 2xx is the one success step.
            showSuccess();
            return null;
          }
          if (res.status === 400) {
            // Only input-validity errors surface. Parse the reason if offered.
            return res.json().catch(() => null).then(body => {
              showError(msg('rejectedEmail'));
              if (emailInput) emailInput.focus();
              return null;
            });
          }
          if (res.status === 429) {
            showError(msg('rateLimited'));
            return null;
          }
          showError(msg('server'));
          return null;
        }).catch(() => {
          // Network failure / timeout: honest message, typed input PRESERVED
          // (we never clear the field on any failure path).
          showError(msg('network'));
        }).finally(() => {
          if (timer) clearTimeout(timer);
          if (btn) {
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
            btn.textContent = originalLabel;
          }
        });
      });
    });
  }

  /* ---------- the popup shell: triggers, focus trap, teaser ------------------ */

  function initPopup(popup) {
    if (popup.dataset.ksuBound) return;
    popup.dataset.ksuBound = '1';

    const dialog = popup.querySelector('[data-ksu-dialog]');
    const teaser = document.querySelector('[data-ksu-teaser]');
    // Declared up here (not at the bar's own block) because openPopup below
    // may run after an early suppression return — a `const` past that return
    // would still be in TDZ and throw.
    const bar = document.querySelector('[data-ksu-mbar]');
    const barDelayMs = parseInt(popup.dataset.ksuMbarDelayMs, 10);
    const barScrollPct = parseInt(popup.dataset.ksuMbarScrollPct, 10);
    // /cart, /checkout*, /account*: purchase and account surfaces never get
    // the invitation bar (the popup engine's rules are unchanged there).
    let barDone = /^\/(cart\/?$|checkout|account)/i.test(String(location.pathname));
    if (!dialog) return;

    if (hasCookie(COOKIE_SUBSCRIBED)) {
      // A subscriber never sees the sheet (or the bar, or the teaser) again.
      // Stamped as a literal: the sheet consts live below this return.
      popup.dataset.ksuSheetMode = 'off';
      document.querySelectorAll('[data-ksu-footer-invitation]').forEach(el => { el.hidden = true; });
      return;
    }

    const dismissDays = parseInt(popup.dataset.ksuDismissDays, 10) || DEFAULT_DISMISS_DAYS;
    const delayMs = parseInt(popup.dataset.ksuTriggerDelayMs, 10);
    const scrollPct = parseInt(popup.dataset.ksuTriggerScrollPct, 10);
    const exitEnabled = popup.dataset.ksuTriggerExit === '1';
    // Declared BEFORE the sheet block below: stampSheetMode() runs eagerly and
    // reaches isCoarse(), which reads this const — the same TDZ trap the file
    // already documents for `bar` above the early returns.
    const coarseQuery = window.matchMedia && window.matchMedia('(max-width: 767px), (pointer: coarse)');

    /* ---------- phone bottom sheet (James, 26 Oct 2026) ----------------------
     * Phones get the automatic invitation back — as a gentle BOTTOM SHEET that
     * replaces the slim bar, not alongside it. Presence of data-ksu-sheet-delay-ms
     * is the enable switch (and its value the visible-time trigger, OR'd with a
     * scroll percent, like every other trigger here). The quiet window is wider
     * than desktop's: once per browser session at most, 14 days after a close,
     * never before 5s of visible time, never over a buy control, never while
     * any other overlay is open. A sheet that interrupts buying or repeats
     * itself is the "too annoying" version this brief rules out. */
    const sheetDelayMs = parseInt(popup.dataset.ksuSheetDelayMs, 10);
    const sheetEnabled = !isNaN(sheetDelayMs) && sheetDelayMs > 0;
    const sheetScrollPct = parseInt(popup.dataset.ksuSheetScrollPct, 10);
    const sheetDismissDays = parseInt(popup.dataset.ksuSheetDismissDays, 10) || DEFAULT_SHEET_DISMISS_DAYS;
    // /cart, /checkout*, /account* are purchase and account surfaces — no sheet there either.
    const purchaseSurface = /^\/(cart\/?$|checkout|account)/i.test(String(location.pathname));
    // Test/contract surface: 'on' when the sheet path is live on this device,
    // 'off' when it is disabled or spent here (desktop, /cart, dismissed, or
    // this browser session already had its one sheet).
    function sheetActive() {
      return sheetEnabled && isCoarse() && !purchaseSurface &&
        !hasCookie(COOKIE_SUBSCRIBED) && !hasCookie(COOKIE_DISMISSED) &&
        !sessionSheetShown();
    }
    function stampSheetMode() {
      popup.dataset.ksuSheetMode = sheetActive() ? 'on' : 'off';
    }
    function sessionSheetShown() {
      return safe(() => sessionStorage.getItem(SESSION_SHEET_SHOWN) === '1', false);
    }
    function markSessionSheetShown() {
      safe(() => sessionStorage.setItem(SESSION_SHEET_SHOWN, '1'));
    }
    // The dismissal window is device-specific: phones hold it 14 days, desktop 7.
    function dismissDaysNow() { return (isCoarse() && sheetEnabled) ? sheetDismissDays : dismissDays; }
    stampSheetMode();

    let open = false;
    let fired = false; // consumed only after an automatic invitation actually opens
    let lastFocus = null;
    let visibleElapsed = 0;
    let visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
    let autoArmed = false;
    let delayTimer = null;
    let scrollHandler = null;
    let scrollMet = false;
    let openedAutomatically = false;

    function isCoarse() { return !!(coarseQuery && coarseQuery.matches); }
    function visibleMs() {
      return visibleElapsed + (visibleSince === null ? 0 : Date.now() - visibleSince);
    }
    function isEditableFocus() {
      const el = document.activeElement;
      return !!(el && (el.matches('input, textarea, select') || el.isContentEditable));
    }
    // Shared "is this node actually on screen" test — size, visibility, and the
    // ancestor walk for shells themes hide instead of the node itself.
    function overlayVisible(el) {
      if (!el.getClientRects().length) return false;
      const visibility = getComputedStyle(el).visibility;
      if (visibility === 'hidden' || visibility === 'collapse') return false;
      // Themes commonly hide a modal's outer shell rather than its inner dialog.
      for (let node = el; node; node = node.parentElement) {
        if (node.hidden || node.getAttribute('aria-hidden') === 'true' ||
            getComputedStyle(node).opacity === '0') return false;
      }
      return true;
    }
    /* ---------- the Shopify Customer Privacy banner (preview finding, 26 Oct) --
     * "COOKIE CONSENT … Accept / Decline" (#shopify-pc__banner, injected late,
     * removed on Accept/Decline). It is just another overlay: while it — or its
     * preferences dialog — is on screen, the sheet and the desktop popup wait,
     * and the existing re-checks (rAF on chrome-change mutations, focusout,
     * visibility) ask again once it goes. After the answer we still hold the
     * invitation PRIVACY_GRACE_MS: the moment is NOT reset, but the sheet must
     * not land on the same frame the banner leaves — two prompts swapping seats
     * reads as one popup replacing another, and the 26 Oct preview finding is
     * exactly that "too annoying" shape. */
    const PRIVACY_GRACE_MS = 1500;
    const PRIVACY_SELECTOR = '#shopify-pc__banner, #shopify-pc__prefs, [id^="shopify-pc__"][role="dialog"]';
    let privacyLastSeen = 0; // Date.now() of the last check that saw it up (0 = never)
    function privacyConsentUp() {
      const up = Array.prototype.some.call(
        document.querySelectorAll(PRIVACY_SELECTOR), overlayVisible
      );
      const now = Date.now();
      if (up) privacyLastSeen = now;
      return up || (privacyLastSeen !== 0 && now - privacyLastSeen < PRIVACY_GRACE_MS);
    }
    function otherOverlayOpen() {
      return privacyConsentUp() || Array.prototype.some.call(document.querySelectorAll(
        'dialog[open], [role="dialog"][aria-modal="true"], [data-modal], [data-cart-drawer], [data-search-drawer], [data-menu-drawer]'
      ), el => {
        if (popup.contains(el)) return false;
        return overlayVisible(el);
      });
    }

    function focusables() {
      return Array.prototype.slice.call(
        dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => el.offsetParent !== null || el === document.activeElement);
    }

    // Wrapped in safe() like every other handler here, and for a sharper reason than
    // the rest: this is a CAPTURE-phase listener on `document`, so it runs before any
    // page-level key handler. An uncaught throw from it is not a silent "no popup" —
    // it is jank on a key the visitor actually pressed, and can abort dispatch of that
    // event for the whole document. Reachable if another script on the page detaches
    // or re-parents the dialog while it is open (theme JS, a third-party app): the
    // focus() calls below then run against a detached node, which throws in some
    // engines. Low probability, page-visible if hit — line review 2026-07-25.
    function onKeydown(ev) {
      safe(() => {
        if (!open) return;
        if (ev.key === 'Escape') {
          ev.preventDefault();
          close(true);
          return;
        }
        if (ev.key !== 'Tab') return;
        const els = focusables();
        if (!els.length) return;
        const first = els[0];
        const last = els[els.length - 1];
        if (ev.shiftKey && document.activeElement === first) {
          ev.preventDefault();
          last.focus();
        } else if (!ev.shiftKey && document.activeElement === last) {
          ev.preventDefault();
          first.focus();
        }
      });
    }

    function openPopup(isManual) {
      if (open || hasCookie(COOKIE_SUBSCRIBED)) return;
      const suppress = !!document.querySelector(
        '[data-ksu-root][data-ksu-layout="embed"], [data-ksu-suppress-popup]'
      );
      if (suppress) return;
      open = true;
      openedAutomatically = !isManual;
      lastFocus = document.activeElement;
      popup.hidden = false;
      // Phones wear the dialog as a bottom sheet (see the sheet CSS block); every
      // other device keeps the centred card. Decided at OPEN, not in a media
      // query, because the trigger engine above already knows the mode.
      popup.classList.toggle('ksu-popup--sheet', isCoarse());
      if (teaser) teaser.hidden = true;
      if (bar) bar.hidden = true; // the bar is an invitation to THIS dialog
      document.documentElement.classList.add('ksu-lock');
      document.addEventListener('keydown', onKeydown, true);
      // EVERY open — automatic included — moves focus into the dialog. An
      // aria-modal="true" dialog that leaves focus on the page behind it is
      // unreachable by keyboard and hides the rest of the document from
      // assistive tech at the same time. The target is the CLOSE control, never
      // the email field: an uninvited invitation must not open a keyboard and
      // put the caret in an input the visitor did not ask for.
      const target = dialog.querySelector('[data-ksu-close]') || focusables()[0];
      if (target && typeof target.focus === 'function') {
        safe(() => target.focus());
      } else {
        safe(() => { dialog.setAttribute('tabindex', '-1'); dialog.focus(); });
      }
      // Keyboard lift: when the visitor taps the email field, the soft keyboard
      // takes half the screen — keep the field (and its submit button) in the
      // remaining half. Only reacts to a focus the visitor initiated; the
      // automatic open never touches the field.
      safe(() => {
        const em = dialog.querySelector('input[name="email"]');
        if (em && !em.__ksuKb) {
          em.__ksuKb = true;
          em.addEventListener('focus', () => safe(() => {
            if (em.scrollIntoView) em.scrollIntoView({ block: 'center', behavior: 'auto' });
          }));
        }
      });
    }

    function close(dismissed) {
      if (!open) return;
      open = false;
      popup.hidden = true;
      document.documentElement.classList.remove('ksu-lock');
      document.removeEventListener('keydown', onKeydown, true);
      if (dismissed) {
        setCookie(COOKIE_DISMISSED, dismissDaysNow());
        // Dismissal is forever on this device: nothing here may open again,
        // so the banner watch goes with it (fix-3 item 2).
        syncPrivacyWatch();
        // Teaser appears after dismissal, UNLESS the visitor has since
        // subscribed (success step left visible state handled there).
        if (teaser && !isCoarse() && !hasCookie(COOKIE_SUBSCRIBED)) teaser.hidden = false;
      }
      const returnTo = lastFocus;
      lastFocus = null;
      if (returnTo && returnTo.isConnected !== false && typeof returnTo.focus === 'function') {
        safe(() => returnTo.focus());
      }
      // Nothing focusable to return to (an automatic open starts from <body>):
      // leave focus stranded inside the now-hidden dialog and the keyboard has
      // nowhere to go. Drop it back to the document instead.
      if (dialog.contains(document.activeElement)) safe(() => document.activeElement.blur());
    }

    // Exposed for the form's NO, THANKS button.
    popup.__ksuDismiss = () => close(true);
    // Exposed for showSuccess(): a subscriber sees nothing further, so the
    // banner watch is spent there too (fix-3 item 2).
    popup.__ksuSpent = () => syncPrivacyWatch();
    popup.__ksuOpen = () => {
      if (hasCookie(COOKIE_SUBSCRIBED) || document.querySelector(
        '[data-ksu-root][data-ksu-layout="embed"], [data-ksu-suppress-popup]'
      )) return false;
      disarm();
      openPopup(true);
      if (open) fired = true; // an explicit invitation consumes this page's automatic prompt
      // On a phone the same courtesy spends the SHEET prompt too (and its
      // once-per-session flag): a visitor who opened the sheet from the footer
      // link must not then be auto-sheeted for it.
      if (open && sheetEnabled && isCoarse()) {
        sheetFired = true;
        markSessionSheetShown();
        stampSheetMode();
        disarmSheet();
      }
      // A manual open consumes this page's automatic prompt on either engine,
      // and the visitor is already in the dialog — stop the banner watch (fix-3 item 2).
      if (open) syncPrivacyWatch();
      return open;
    };

    popup.querySelectorAll('[data-ksu-close]').forEach(el => {
      el.addEventListener('click', () => close(true));
    });

    if (teaser) {
      teaser.addEventListener('click', () => {
        popup.__ksuOpen();
      });
    }

    // Keep the real destination when the popup cannot be opened. This handler is
    // intentionally installed only after initPopup has exposed __ksuOpen.
    document.querySelectorAll('[data-ksu-open]').forEach(link => {
      link.addEventListener('click', ev => {
        if (popup.__ksuOpen && popup.__ksuOpen()) ev.preventDefault();
      });
    });

    // Suppression: never auto-fire for a visitor we already captured here.
    // (Signal 1 — logged-in customers — is handled in Liquid: the popup markup
    // is not rendered at all.) A dismissal shows the teaser instead.
    if (hasCookie(COOKIE_SUBSCRIBED)) return;
    // An overlay obscures the inline form and loses its campaign attribution.
    // data-ksu-suppress-popup is the same opt-out for a page that carries its own
    // signup call to action without embedding the form (the Koban rewards page
    // links to the newsletter page instead of rendering a second form).
    if (document.querySelector(
      '[data-ksu-root][data-ksu-layout="embed"], [data-ksu-suppress-popup]'
    )) return;
    if (hasCookie(COOKIE_DISMISSED)) {
      if (teaser && !isCoarse()) teaser.hidden = false;
      return;
    }

    function canAutoOpen() {
      return !fired && !open && !isCoarse() && !hasCookie(COOKIE_DISMISSED) &&
        !hasCookie(COOKIE_SUBSCRIBED) && document.visibilityState === 'visible' &&
        !isEditableFocus() && !otherOverlayOpen();
    }
    // ANY-of (2026-09-28): an exit, 30s visible, or 50% scroll — the AND of
    // delay+scroll installed 10 Sep cost ~80% of signups and is gone. The
    // safeguards above stay.
    function fire(trigger) {
      if (!canAutoOpen()) return false;
      if (trigger !== 'exit' && !scrollMet && (isNaN(delayMs) || visibleMs() < delayMs)) return false;
      fired = true;
      disarm();
      syncPrivacyWatch(); // the desktop prompt is spent — stop the watch (fix-3 item 2)
      openPopup(false); // automatic: focus goes to the close control, not the field
      return open;
    }

    function onScroll() {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) return;
      scrollMet = (window.scrollY || doc.scrollTop || 0) / total * 100 >= scrollPct;
      if (scrollMet) fire('delay');
    }
    // Exit intent: pointer leaves the viewport at the TOP (toward the chrome).
    // Desktop-only signal — mobile has no reliable equivalent, which is fine:
    // ANY-of means delay/scroll still cover mobile.
    function onMouseOut(ev) {
      if (ev.clientY <= 0 && !ev.relatedTarget) fire('exit');
    }

    function arm() {
      if (autoArmed || isCoarse() || fired) return;
      autoArmed = true;
      syncPrivacyWatch();
      if (!isNaN(delayMs) && delayMs > 0) delayTimer = setTimeout(() => fire('delay'), delayMs);
      if (!isNaN(scrollPct) && scrollPct > 0 && scrollPct <= 100) {
        scrollHandler = () => safe(() => onScroll());
        window.addEventListener('scroll', scrollHandler, { passive: true });
      }
      if (exitEnabled) document.addEventListener('mouseout', onMouseOut);
    }
    function disarm() {
      autoArmed = false;
      if (delayTimer) { clearTimeout(delayTimer); delayTimer = null; }
      if (scrollHandler) window.removeEventListener('scroll', scrollHandler);
      document.removeEventListener('mouseout', onMouseOut);
    }

    function onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        visibleSince = Date.now();
        fire('delay');
      } else if (visibleSince !== null) {
        visibleElapsed += Date.now() - visibleSince;
        visibleSince = null;
      }
    }
    function onFocusOut() { setTimeout(() => fire('delay'), 0); }
    function onModeChange() {
      // React to a coarse<->fine FLIP only (review F1/F2, 26 Oct): Chrome
      // Android fires window.resize when the soft keyboard opens, and a
      // rotation keeps the device coarse — both used to close an in-use sheet
      // here on every event. A resize that stays in the current mode is a
      // no-op for open/close: the sheet stays open where it is.
      const nowCoarse = isCoarse();
      if (nowCoarse !== wasCoarse) {
        wasCoarse = nowCoarse;
        syncPrivacyWatch(); // a mode flip disarms both engines — re-sync the watch (fix-3 item 2)
        if (nowCoarse) {
          disarm();
          if (open && openedAutomatically) close(false);
          if (teaser) teaser.hidden = true;
          if (sheetEnabled && !sheetFired && !open) armSheet();
        } else if (!fired && !hasCookie(COOKIE_DISMISSED)) {
          arm();
          // A sheet that auto-opened on a phone rotates out with the phone: the
          // desktop popup keeps its own engine; the sheet never owns a fine-pointer screen.
          if (open && openedAutomatically && openedAsSheet) close(false);
          disarmSheet();
        }
      }
      stampSheetMode();
      safe(syncBar);
    }

    /* ---------- mobile invitation bar (phones / coarse pointers) -------------
     * The automatic modal never fires here (arm() refuses coarse pointers); the
     * bar is the invitation instead. JOIN opens THIS popup through the manual
     * path, so the form still posts source=popup. Geometry contract: the bar is
     * a fixed, viewport-centred pill whose column never crosses either launcher
     * column (CSS width min(214px, 100vw - 176px) keeps >=8px clear of the
     * ~68px Google widget on the left and the ~60px chat launcher 20px off the
     * right at any width 320-767), lifted 12px above the top of any FIXED
     * purchase cluster (sticky ATC panel / product form / bottom nav) — and
     * hidden outright when that cluster is tall enough to own the band the bar
     * would sit in. It never resizes or moves the launchers, so
     * kns-widget-clearance.js never has to know it exists (its launcher x-
     * columns are position-invariant, and our x-range is). (The bar's state —
     * `bar`, `barDelayMs`, `barScrollPct`, `barDone` — is declared at the top
     * of initPopup, above the early-return guards, for the TDZ reason noted
     * there.) */

    function barScrollMet() {
      if (isNaN(barScrollPct) || !(barScrollPct > 0)) return false;
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) return false;
      return (window.scrollY || doc.scrollTop || 0) / total * 100 >= barScrollPct;
    }
    function barPinned(node) {
      for (let n = node; n && n !== document.documentElement; n = n.parentElement) {
        const cs = getComputedStyle(n);
        if (cs.visibility === 'hidden' || cs.visibility === 'collapse' || cs.opacity === '0') return false;
        if (cs.display === 'none') return false;
        const pos = cs.position;
        if (pos === 'fixed' || pos === 'sticky') return true;
      }
      return false;
    }
    // A launcher that EXPANDS across the bar's row (a wide Google label) is
    // caught by a hit-test, not by arithmetic: if either launcher's own node is
    // on the bar at any of its three sampling points, the bar yields.
    function barLauncherIntrusion() {
      const r = bar.getBoundingClientRect();
      if (!r.width || !r.height) return false;
      const midY = (r.top + r.bottom) / 2;
      const pts = [[r.left + 8, midY], [r.right - 8, midY], [(r.left + r.right) / 2, midY]];
      for (let i = 0; i < pts.length; i++) {
        const x = pts[i][0], y = pts[i][1];
        if (x < 0 || y < 0 || x >= window.innerWidth || y >= window.innerHeight) continue;
        const stack = document.elementsFromPoint ? document.elementsFromPoint(x, y) : [document.elementFromPoint(x, y)];
        for (let s = 0; s < stack.length; s++) {
          const node = stack[s];
          if (node && node.closest && node.closest('#google-merchantwidget-iframe-wrapper, #kns-chat-launcher')) return true;
        }
      }
      return false;
    }
    // The bar never sits on an IN-PAGE buy control either. barPurchaseTop() lifts
    // it above FIXED purchase chrome only; an inline product form scrolled into the
    // bar's band was covered (10 Oct 2026, .co.kr knife-guard page at 390x504: the
    // bar lay over Qty + Add to cart). It steps aside while it overlaps one and comes
    // back once that control scrolls clear.
    function buyControls() {
      // .shopify-payment-button / shopify-accelerated-checkout: the "Buy it now" / Shop Pay
      // button renders with no light-DOM <button>, so the wrapper itself is the target
      // (10 Oct 2026, AU saya page at 320x432: the bar lay across it).
      return document.querySelectorAll(BUY_CONTROL_SELECTOR);
    }
    function barOverBuyControl() {
      const r = bar.getBoundingClientRect();
      if (!r.width || !r.height) return false;
      const ctrls = buyControls();
      for (let i = 0; i < ctrls.length; i++) {
        const c = ctrls[i].getBoundingClientRect();
        if (c.width > 0 && c.height > 0 && c.left < r.right && c.right > r.left && c.top < r.bottom && c.bottom > r.top) return true;
      }
      return false;
    }
    function barPurchaseTop() {
      let top = null;
      document.querySelectorAll('.sticky-atc-panel, [data-sticky-atc], [data-mobile-bottom-nav], .js-product-form, product-form')
        .forEach(el => {
          if (!el.getClientRects().length) return;
          const r = el.getBoundingClientRect();
          if (r.width <= 0 || r.height <= 0 || r.bottom <= 0 || r.top >= window.innerHeight) return;
          if (!barPinned(el)) return; // only FIXED chrome blocks the bar band
          top = top === null ? r.top : Math.min(top, r.top);
        });
      return top;
    }
    function placeBar() {
      const top = barPurchaseTop();
      const r = bar.getBoundingClientRect();
      if (top === null) { bar.style.removeProperty('bottom'); return; } // CSS rest: safe-area + 12px
      const bottom = window.innerHeight - top + 12;
      // Cluster so tall the bar would be pushed off the top: the purchase
      // controls own the screen — the invitation waits, it never collides.
      if (window.innerHeight - bottom - r.height < 0) { bar.hidden = true; return; }
      bar.style.bottom = Math.round(bottom) + 'px';
    }
    function syncBar() {
      if (!bar || barDone) return;
      if (hasCookie(COOKIE_DISMISSED) || hasCookie(COOKIE_SUBSCRIBED) ||
          document.querySelector('[data-ksu-root][data-ksu-layout="embed"], [data-ksu-suppress-popup]')) {
        barDone = true;
        bar.hidden = true;
        return;
      }
      // Moment = coarse pointer, threshold met, and nothing else on screen:
      // not our own popup, not the cart/menu/search drawer, not any dialog.
      // The sheet replaces the bar on phones: when it is enabled, the bar is
      // gone for good (the brief's "not too annoying" — one invitation, not two).
      if (sheetEnabled) { barDone = true; bar.hidden = true; return; }
      const moment = !!bar && isCoarse() && !open && !otherOverlayOpen() &&
        ((visibleMs() >= barDelayMs && barDelayMs > 0) || barScrollMet());
      bar.hidden = !moment;
      if (moment) {
        placeBar();
        if (barLauncherIntrusion() || barOverBuyControl()) bar.hidden = true;
      }
    }

    // Sheet mode retires the bar for good (syncBar would set this on its first run anyway): set it BEFORE the
    // bar's whole-document MutationObserver below is armed, so phones in sheet mode never run it (chair, 26 Oct).
    if (sheetEnabled) barDone = true;
    if (bar && !barDone) {
      // Guard each control (review N1): a bar missing a button must degrade to "no invitation",
      // never throw here and take the rest of initPopup (desktop popup, teaser, triggers) with it.
      const barOpen = bar.querySelector('[data-ksu-mbar-open]');
      const barClose = bar.querySelector('[data-ksu-mbar-close]');
      if (barOpen) barOpen.addEventListener('click', () => safe(() => {
        popup.__ksuOpen(); // manual path: source stays 'popup', and openPopup hides the bar
        syncBar();         // a refused open (e.g. subscribed meanwhile) must not leave the bar stale
      }));
      if (barClose) barClose.addEventListener('click', () => safe(() => {
        barDone = true; // gone for good — and the popup's own 7-day window now applies too
        setCookie(COOKIE_DISMISSED, dismissDays);
        bar.hidden = true;
        syncPrivacyWatch(); // dismissal forever: stop the banner watch (fix-3 item 2)
      }));
      // syncBar reads layout (rects, hit-tests). Never run it inside a
      // mutation callback or a scroll handler directly — one coalesced rAF
      // per burst, off the observer's synchronous path.
      let barFrame = 0;
      const scheduleBar = () => {
        if (barFrame || barDone) return;
        barFrame = window.requestAnimationFrame(() => { barFrame = 0; safe(syncBar); });
      };
      window.addEventListener('scroll', scheduleBar, { passive: true });
      document.addEventListener('focusout', () => safe(syncBar));
      document.addEventListener('visibilitychange', () => safe(syncBar));
      if (!isNaN(barDelayMs) && barDelayMs > 0) setTimeout(() => safe(syncBar), barDelayMs);
      // The drawers/dialogs toggle their own attributes, and theme JS injects
      // the sticky ATC panel late; one observer turns "the chrome changed"
      // into a re-place/re-show without polling.
      const barRelevant = el => !!(el && el.nodeType === 1 && (
        el.closest('#cart-drawer, cart-drawer, side-drawer, [data-cart-drawer], [data-search-drawer], [data-menu-drawer], dialog, [role="dialog"], .sticky-atc-panel, [data-sticky-atc], [data-mobile-bottom-nav], .js-product-form, product-form, #google-merchantwidget-iframe-wrapper, #kns-chat-launcher') ||
        el.matches('#cart-drawer, cart-drawer, side-drawer, [data-cart-drawer], [data-search-drawer], [data-menu-drawer], dialog, [role="dialog"], .sticky-atc-panel, [data-sticky-atc], [data-mobile-bottom-nav], .js-product-form, product-form, #google-merchantwidget-iframe-wrapper, #kns-chat-launcher')));
      new MutationObserver(records => {
        for (let i = 0; i < records.length; i++) {
          const r = records[i];
          const hit = r.type === 'attributes' ? barRelevant(r.target) :
            Array.prototype.some.call(r.addedNodes, n => (n.parentElement && barRelevant(n)) || barRelevant(n));
          if (hit) { scheduleBar(); return; }
        }
      }).observe(document.documentElement, { subtree: true, attributes: true, childList: true });
    }

    /* ---------- the phone bottom sheet: trigger engine ------------------------
     * Same dialog, same submit, same a11y — the SHEET is what kns-signup.css
     * makes of .ksu-dialog at phone widths. This block only decides WHEN it may
     * open automatically, mirroring syncBar's shape: one coalesced rAF per
     * burst, "wait and re-check" instead of "open on top of a buy control". */
    let sheetArmed = false;
    let sheetFired = false; // this page's automatic sheet is spent
    let sheetDelayTimer = null;
    let sheetScrollHandler = null;
    let sheetFrame = 0;
    let sheetNudges = false; // the sheet's focusout/visibility nudges are installed
    let sheetNudgeHandlers = null; // named refs so disarmSheet can remove them (review F5)
    let openedAsSheet = false; // the open dialog IS the phone sheet (mode-aware close needs the fact, not the current mode)
    // Previous coarse state, so onModeChange closes only on a REAL mode FLIP
    // (review F1/F2, 26 Oct): an Android soft keyboard fires window.resize, and
    // a portrait<->landscape rotation keeps the device coarse — either one
    // shutting an in-use sheet was the HIGH/MED finding.
    let wasCoarse = isCoarse();

    function sheetBuyControlInViewport() {
      // VIEWPORT test (not a sheet rect — the sheet does not exist yet here).
      // FIXED/STICKY buy chrome (sticky ATC panel, [data-sticky-atc], bottom
      // nav — what barPinned() calls pinned) is IGNORED (chair decision,
      // review F3): such a panel is always on screen on a PDP, so counting it
      // would hold the sheet down forever. Only an IN-PAGE buy control the
      // visitor could be mid-purchase on makes the sheet wait.
      const vw = window.innerWidth, vh = window.innerHeight;
      const ctrls = buyControls();
      for (let i = 0; i < ctrls.length; i++) {
        const c = ctrls[i].getBoundingClientRect();
        if (c.width > 0 && c.height > 0 && c.left < vw && c.right > 0 && c.top < vh && c.bottom > 0 &&
            !barPinned(ctrls[i])) return true;
      }
      return false;
    }
    function sheetScrollMet() {
      if (isNaN(sheetScrollPct) || !(sheetScrollPct > 0)) return false;
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) return false;
      return (window.scrollY || doc.scrollTop || 0) / total * 100 >= sheetScrollPct;
    }
    function sheetMoment() {
      if (sheetFired || sessionSheetShown() || !sheetActive()) return false;
      if (open || otherOverlayOpen() || isEditableFocus()) return false;
      // The scroll trigger may arrive early; the 5s floor still applies to it.
      if (sheetScrollMet()) return visibleMs() >= SHEET_MIN_VISIBLE_MS;
      return visibleMs() >= Math.max(sheetDelayMs, SHEET_MIN_VISIBLE_MS);
    }
    function syncSheet() {
      if (!sheetEnabled) return;
      stampSheetMode();
      if (!sheetMoment()) return;
      // A buy control on screen: hold the sheet down and re-check on the next
      // scroll / chrome change — the same courtesy the bar pays its PDP.
      if (sheetBuyControlInViewport()) return;
      const wasOpen = open;
      openPopup(false); // automatic: focus goes to the close control, not the field
      if (!wasOpen && open) {
        sheetFired = true;
        openedAsSheet = true;
        markSessionSheetShown();
        stampSheetMode(); // 'off' from here: once per session is spent
        disarmSheet();
      }
    }
    function scheduleSheet() {
      if (sheetFrame || sheetFired) return;
      sheetFrame = window.requestAnimationFrame(() => { sheetFrame = 0; safe(syncSheet); });
    }
    // Shopify's privacy banner is injected late and REMOVED on Accept/Decline —
    // neither of which the nudges below reliably notice (a tap on a banner
    // button, no focus was in an input, the tab never hid). This watch turns
    // "the consent chrome appeared/vanished/changed class" into one re-check of
    // BOTH waiting engines, coalesced like every other burst here. A miss only
    // ever means "wait", never "throw": same never-throw contract as the rest.
    // SCOPE (fix-3 perf, 26 Oct): the first cut observed `documentElement` with
    // subtree+attributes and never disconnected, so every class/style flip in
    // a carousel, animation or the theme's own scripts paid a callback and an
    // ancestor walk for the whole page life — on EVERY page view, for a banner
    // that appears once. Now: (a) `document.body` with childList only (no
    // subtree) — Shopify appends the banner to body, and body's direct children
    // are where an injected/removed banner can be seen at all; (b) once the
    // element is located, an attribute watch on THAT element alone. If a Shopify
    // install ever injects the banner deeper than body's direct children, (a)
    // simply never reports it and the gate still holds — `privacyConsentUp()`
    // runs inside `otherOverlayOpen()`, which every existing nudge re-runs on
    // the next rAF (focusout from the Accept/Decline tap, visibilitychange, the
    // delay timer, scroll): the fallback is the existing rAF re-check, chosen
    // over a second subtree walk because a missed nudge only ever delays.
    let privacyFrame = 0;
    let privacyBodyObserver = null;   // document.body, childList only
    let privacyAttrObserver = null;   // the banner element itself, nothing else
    let privacyWatched = null;        // the element (re)attached above
    function scheduleAfterPrivacy() {
      if (privacyFrame) return;
      privacyFrame = window.requestAnimationFrame(() => {
        privacyFrame = 0;
        safe(scheduleSheet);
        safe(() => { if (!isCoarse()) fire('delay'); });
      });
    }
    // Test/contract counter only (brief S14): how many times a privacy-watch
    // callback has run. A page has no use for it; the probe asserts it stops.
    window.__ksuPrivacyChecks = 0;
    // Cheap by construction: no ancestor walk (the walk was the hot part). A
    // body-level record is relevant when the node IS consent chrome or carries
    // it as a descendant of the direct body child Shopify appends.
    const privacyNode = n => !!(n && n.nodeType === 1 && (
      (n.id || '').indexOf('shopify-pc__') === 0 ||
      (n.matches && n.matches(PRIVACY_SELECTOR)) ||
      (n.querySelector && n.querySelector(PRIVACY_SELECTOR))));
    function unwatchPrivacyEl() {
      if (privacyAttrObserver) privacyAttrObserver.disconnect();
      privacyAttrObserver = null;
      privacyWatched = null;
    }
    // (b) Point the single-element attribute watch at the banner, if present.
    // Re-attaches when the element is replaced (next body childList record) or
    // vanishes; no-ops once the watch is spent (observers are gone).
    function rescanPrivacyEl() {
      const el = document.querySelector(PRIVACY_SELECTOR);
      if (el === privacyWatched) return;
      unwatchPrivacyEl();
      if (!el || !privacyBodyObserver || typeof MutationObserver === 'undefined') return;
      privacyWatched = el;
      privacyAttrObserver = new MutationObserver(() => {
        window.__ksuPrivacyChecks++;
        if (privacyWatched && !privacyWatched.isConnected) { unwatchPrivacyEl(); return; }
        scheduleAfterPrivacy();
      });
      privacyAttrObserver.observe(el, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    }
    // (a) Body-level inject/remove of the banner.
    function armPrivacyWatch() {
      if (privacyBodyObserver || typeof MutationObserver === 'undefined') return;
      privacyBodyObserver = new MutationObserver(records => {
        for (let i = 0; i < records.length; i++) {
          const r = records[i];
          // addedNodes OR removedNodes: Accept/Decline REMOVES the banner, and
          // "it just vanished" is exactly the moment the waiting invitation must
          // be re-asked about (grace still holds it for PRIVACY_GRACE_MS).
          const hit = Array.prototype.some.call(r.addedNodes, privacyNode) ||
            Array.prototype.some.call(r.removedNodes || [], privacyNode);
          if (hit) { window.__ksuPrivacyChecks++; safe(rescanPrivacyEl); scheduleAfterPrivacy(); return; }
        }
      });
      privacyBodyObserver.observe(document.body, { childList: true });
      safe(rescanPrivacyEl); // a banner already on screen at boot (S12 fixture)
    }
    function disarmPrivacyWatch() {
      if (privacyBodyObserver) { privacyBodyObserver.disconnect(); privacyBodyObserver = null; }
      unwatchPrivacyEl();
    }
    // One gate for every "may an invitation still earn its open here?" change
    // (fix-3 item 2): the watch exists exactly while THIS device has an
    // automatic prompt left to spend — coarse → the sheet engine (same arm
    // condition d3fc02b used, so no new observer on a phone); fine → the desktop
    // popup, whose arm()/fire() run the sync too (a body-childList record is a
    // rare, cheap event; the subtree+attribute watch it replaces was the cost).
    function privacyWatchNeeded() {
      if (purchaseSurface) return false;
      return isCoarse() ? (sheetActive() && !sheetFired && !open) : !fired;
    }
    // "Spent for good" is a DEVICE fact — subscribed, or dismissed under the
    // 7/14-day window — so only those latch. A mode flip disarms for now, and a
    // flip back to a live phone re-arms: same page, the banner may still arrive.
    let privacyWatchSpent = false;
    function syncPrivacyWatch() {
      if (hasCookie(COOKIE_SUBSCRIBED) || hasCookie(COOKIE_DISMISSED)) privacyWatchSpent = true;
      if (privacyWatchSpent || !privacyWatchNeeded()) return disarmPrivacyWatch();
      armPrivacyWatch();
    }
    function armSheet() {
      if (sheetArmed || sheetFired || !sheetActive() || sessionSheetShown()) return;
      sheetArmed = true;
      sheetDelayTimer = setTimeout(() => safe(syncSheet), Math.max(sheetDelayMs, 0));
      if (!isNaN(sheetScrollPct) && sheetScrollPct > 0 && sheetScrollPct <= 100) {
        sheetScrollHandler = () => safe(scheduleSheet);
        window.addEventListener('scroll', sheetScrollHandler, { passive: true });
      }
      // The banner watch itself is owned by syncPrivacyWatch (boot, mode flips,
      // spend points) — armSheet no longer arms it directly (fix-3 item 2).
      // The bar's "the moment may have arrived" nudges, mirrored: returning to
      // the tab or leaving an input re-asks the question, one rAF per burst.
      // Added once — armSheet can run again after a mode flip.
      if (!sheetNudges) {
        sheetNudges = true;
        sheetNudgeHandlers = {
          focusout: () => safe(scheduleSheet),
          visibilitychange: () => safe(scheduleSheet),
        };
        document.addEventListener('focusout', sheetNudgeHandlers.focusout);
        document.addEventListener('visibilitychange', sheetNudgeHandlers.visibilitychange);
      }
    }
    function disarmSheet() {
      syncPrivacyWatch(); // spent-by-sheet (fired / session-shown / dismissed) stops the watch too (fix-3 item 2)
      sheetArmed = false;
      if (sheetDelayTimer) { clearTimeout(sheetDelayTimer); sheetDelayTimer = null; }
      if (sheetScrollHandler) { window.removeEventListener('scroll', sheetScrollHandler); sheetScrollHandler = null; }
      // Drop the nudges on EVERY disarm, not only after a fire (line review,
      // 26 Oct): an EARLY dismiss — the visitor opens the sheet from the footer
      // link, or the mode flips before the sheet ever fired — left two permanent
      // document listeners attached to an engine that just stood down. They
      // could only ever scheduleSheet into a refusal, i.e. pure leak. armSheet
      // re-installs them (sheetNudges-gated) if a mode flip re-arms the engine.
      if (sheetNudgeHandlers) {
        document.removeEventListener('focusout', sheetNudgeHandlers.focusout);
        document.removeEventListener('visibilitychange', sheetNudgeHandlers.visibilitychange);
        sheetNudgeHandlers = null;
        sheetNudges = false;
      }
    }

    arm();
    if (sheetEnabled) armSheet();
    syncPrivacyWatch(); // the watch lives exactly as long as this page has a prompt to spend (fix-3)
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('focusout', onFocusOut);
    window.addEventListener('resize', onModeChange);
    if (coarseQuery && coarseQuery.addEventListener) coarseQuery.addEventListener('change', onModeChange);
  }

  /* ---------- entry point: NEVER throw into the page -------------------------- */

  function boot() {
    document.querySelectorAll('[data-ksu-root]').forEach(el => safe(() => initForm(el)));
    const popup = document.querySelector('[data-ksu-popup]');
    if (popup) safe(() => initPopup(popup));
  }

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => safe(boot));
    } else {
      safe(boot);
    }
  } catch (_) {
    /* Degrade to "no popup". The page must never pay for the capture form. */
  }
})();
